/**
 * rs-infinite-scroll — collection PLP infinite scroll (Roswell Studios).
 *
 * Mirrors Shopify Horizon's request behaviour (Shopify/horizon: paginated-list.js +
 * section-renderer.js) so our footprint matches Shopify's own theme and stays under
 * the Cloudflare storefront bot-challenge that the old Ajaxinate loader tripped. On a
 * standard (non-Plus) plan that challenge is NOT merchant-tunable and hits VPN/shared
 * IPs hardest (Shopify staff, 2026-06-30: no complete VPN fix yet). See ticket
 * TAHARI-ISSUE-12551885871. Vendored ajaxinate.js is left in place, no longer referenced.
 *
 * Request discipline (the part that keeps us under the threshold):
 *   - Section Rendering API (`?section_id=`) — section-only, not full pages.
 *   - Cache + in-flight dedupe by URL: each page URL is fetched AT MOST ONCE per
 *     session, INCLUDING a bot-challenge/error response (cached so we never re-request
 *     it). No retries, no repeated identical requests — the pattern Cloudflare punishes.
 *   - Prefetch one page ahead during idle; render from cache.
 *   - history.pushState keeps ?page in sync (SEO + back button).
 *   - Silent degradation: a response with no product grid stops loading quietly.
 *
 * Trigger: a throttled scroll/resize check (NOT IntersectionObserver). IO only fires
 * on an intersection *change*, so events dropped while a fetch is in flight, or a
 * sentinel left sitting on-screen at the bottom, would stall loading even with the
 * next page cached. A guarded scroll check has no such blind spot. It's cheap and
 * cannot cause extra network requests — renderNext is gated by the loading lock and
 * the cache.
 *
 * Markup contract (sections/collection.liquid): this element is the grid
 * (id="AjaxinateLoop", data-ajaxinate-id); #AjaxinatePagination[data-ajaxinate-id]
 * holds the spinner + a single <a href="{next page}"> while more pages remain.
 */
class RsInfiniteScroll extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.ajaxinateId;
    this.pagination = document.querySelector(
      `#AjaxinatePagination[data-ajaxinate-id="${this.sectionId}"]`
    );
    if (!this.pagination || this.children.length === 0) return;

    this.cache = new Map();
    this.pending = new Map();
    this.loading = false;
    this.blocked = false; // set once a page comes back without a grid (challenge/error)
    this.MARGIN = 600; // px beyond the viewport that counts as "near enough to load"

    this.check = this.check.bind(this);
    // Throttle scroll/resize to one check per animation frame.
    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.check();
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });

    // Prefetch the next page during idle, then do an initial check — covers a short
    // first page whose sentinel is already visible with no scroll to trigger it.
    this.idle(() => {
      this.prefetch(this.nextUrl());
      this.check();
    });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    this.controller && this.controller.abort();
  }

  idle(fn) {
    (window.requestIdleCallback || ((f) => setTimeout(f, 200)))(fn);
  }

  setSpinner(visible) {
    const loader = this.pagination.querySelector('.btn__loader');
    if (loader) loader.style.visibility = visible ? 'visible' : 'hidden';
  }

  // Is the pagination sentinel within the viewport (+ margin)?
  sentinelInView() {
    const r = this.pagination.getBoundingClientRect();
    return r.top < window.innerHeight + this.MARGIN && r.bottom > -this.MARGIN;
  }

  // The next page's full-page URL, read from the pagination link (null on last page).
  nextUrl() {
    const link = this.pagination.querySelector('a');
    return link ? new URL(link.href, window.location.origin) : null;
  }

  // Rewrite a full-page URL to the Section Rendering API endpoint (section-only).
  sectionUrl(url) {
    const u = new URL(url);
    u.searchParams.set('section_id', this.sectionId);
    u.searchParams.sort(); // stable cache key
    return u.toString();
  }

  // Fetch section HTML with cache + in-flight dedupe. Caches whatever comes back —
  // INCLUDING a challenge/error page — so a URL is fetched at most once per session.
  async fetchSection(url) {
    const key = this.sectionUrl(url);
    if (this.cache.has(key)) return this.cache.get(key);
    if (this.pending.has(key)) return this.pending.get(key);

    const promise = fetch(key)
      .then((res) => res.text())
      .finally(() => this.pending.delete(key));
    this.pending.set(key, promise);

    const html = await promise;
    this.cache.set(key, html);
    return html;
  }

  async prefetch(url) {
    if (!url) return;
    try {
      await this.fetchSection(url);
    } catch (_) {
      /* silent; a real render will re-attempt */
    }
  }

  // Render the next page if we're near the bottom and idle. Safe to call as often as
  // scroll/resize/idle fire — the loading lock + cache mean it can never issue a
  // duplicate or retried request.
  check() {
    if (this.blocked || this.loading || !this.isConnected) return;
    if (!this.nextUrl() || !this.sentinelInView()) return;
    this.renderNext();
  }

  async renderNext() {
    if (this.loading) return;
    const url = this.nextUrl();
    if (!url) return;

    this.loading = true;
    this.setSpinner(true);

    try {
      const html = await this.fetchSection(url);
      if (!this.isConnected) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const items = doc.querySelector('#AjaxinateLoop');
      // No product grid = a challenge/error response (already cached). Stop for this
      // session — do NOT retry (that's the request pattern that trips the bot layer).
      if (!items) {
        this.blocked = true;
        return;
      }

      this.append(...Array.from(items.children));
      const nextPagination = doc.querySelector('#AjaxinatePagination');
      this.pagination.innerHTML = nextPagination ? nextPagination.innerHTML : '';
      history.pushState('', '', url.toString());

      if (this.nextUrl()) {
        this.idle(() => this.prefetch(this.nextUrl())); // warm the following page
        // Continue while the sentinel is still near the bottom (tall viewport, or the
        // shopper parked at the bottom). Runs after this render settles; renders come
        // from cache, so no extra network requests.
        requestAnimationFrame(this.check);
      }
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      // Transient network error (not a challenge): nothing cached, a later scroll retries once.
    } finally {
      this.loading = false;
      this.setSpinner(false);
    }
  }
}

if (!customElements.get('rs-infinite-scroll')) {
  customElements.define('rs-infinite-scroll', RsInfiniteScroll);
}
